import { Button, Modal, message } from "antd";
import dayjs from "dayjs";
import { useEffect, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { customerWxService } from "@/api/customerWxService";
import { Breadcrumb } from "@/components/common";
import { refreshUserInfo } from "@/runtime/bootstrap";
import { runtimePublicConfig } from "@/runtime/config";
import { useUserSession } from "@/stores/session";

/** 旧页 dateFormat/dateTimeFormat 均显式传入 LOG_DATE_FORMAT，必须保留到秒。 */
export function formatSafeDate(value?: string) { return value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : ""; }

/** 安全设置首页，承接密码、邮箱和微信三个独立安全入口。 */
export function SafeSettingPage() {
    const customerId = useUserSession(state => state.userInfo.customer.id);
    const email = useUserSession(state => state.email);
    const bindTime = useUserSession(state => state.bindTime);
    const nickName = useUserSession(state => state.nickName);
    const passwordUpdateTime = useUserSession(state => state.passwordUpdateTime);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => { void refreshUserInfo().catch(() => undefined); }, []);
    useEffect(() => {
        const code = new URLSearchParams(location.search).get("code");
        if (!code || !customerId) return;
        void customerWxService.bind({ customerId, code }).then(result => {
            useUserSession.setState({ bindTime: result.bindTime ?? "", nickName: result.nickName ?? "" });
            void message.success("绑定成功");
            navigate(location.pathname, { replace: true });
        }).catch(() => void message.error("微信绑定失败，请稍后重试"));
    }, [customerId, location.pathname, location.search, navigate]);

    /** 跳转微信开放平台，回调地址必须移除当前查询参数。 */
    const bindWechat = () => {
        const redirectUri = window.location.href.split("?")[0];
        window.open(`https://open.weixin.qq.com/connect/qrconnect?appid=${runtimePublicConfig.wxAppId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_login&state=wx-bind#wechat_redirect`);
    };
    /** 解绑前二次确认，成功后同步清空持久化会话。 */
    const unbindWechat = () => Modal.confirm({
        title: "提示",
        content: "确定要解绑微信吗?",
        okText: "确定",
        cancelText: "取消",
        onOk: async () => {
            await customerWxService.untie({ customerId });
            useUserSession.setState({ bindTime: "", nickName: "" });
            void message.success("解绑成功");
        }
    });

    return <section className="safe-setting-page"><div className="section-card safe-setting-heading"><Breadcrumb routes={[{ name: "账号管理" }, { name: "安全设置" }]} /><div className="safe-setting-title">安全设置</div></div><div className="section-card safe-setting-grid">
        <SafeSettingItem icon="icon-password" title="登录密码" status={passwordUpdateTime ? formatSafeDate(passwordUpdateTime) : "密码未设置"} tip="登陆链云账号时需要输入的密码" action={passwordUpdateTime ? "修改" : "设置"} onAction={() => navigate(`/me/safeSetting/updatePassword?type=${passwordUpdateTime ? "update" : "setting"}`)} />
        <SafeSettingItem icon="icon-email" title={email || "安全邮箱"} status={email ? undefined : "邮箱未绑定"} tip="您可以使用邮箱来保障您的账号安全" action={email ? "修改邮箱" : "绑定"} onAction={() => navigate(`/me/safeSetting/updateEmail?type=${email ? "update" : "bind"}`)} />
        <SafeSettingItem icon="icon-wechat1" title="绑定微信" status={bindTime ? "微信已绑定" : "微信未绑定"} tip={bindTime ? <div className="safe-setting-bind"><div><span>绑定账号</span>{nickName || "--"}</div><div><span>绑定时间</span>{formatSafeDate(bindTime)}</div></div> : "绑定微信账号，使用微信账号便捷登录"} action={bindTime ? "解绑" : "绑定"} onAction={bindTime ? unbindWechat : bindWechat} bound={Boolean(bindTime)} />
    </div></section>;
}

/** 对应旧页 card/left/info 结构，图标继续使用控制台 iconfont。 */
function SafeSettingItem({ icon, title, status, tip, action, onAction, bound = false }: { icon: string; title: string; status?: string; tip: ReactNode; action: string; onAction: () => void; bound?: boolean }) {
    return <article className="safe-setting-item"><div className="safe-setting-left"><i className={`iconfont ${icon}`} /><div className={`safe-setting-info${bound ? " is-bound" : ""}`}><div className="safe-setting-item-title"><strong>{title}</strong>{status ? <span>{status}</span> : null}</div><div className="safe-setting-tip">{tip}</div></div></div><Button onClick={onAction}>{action}</Button></article>;
}
